import type { ITemplate } from "@/modules/template/common/types";
import { DeleteTemplateModal, TemplateNameModal } from "@/modules/template/components";
import { useRenameTemplateMutation } from "@/modules/template/services";
import { OperationRoutes } from "@/shared/common/constants";
import { CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import { MoreIcon } from "@/shared/components/SvgIcons";
import { buildRoute } from "@/shared/helpers";
import { useNotify } from "@/shared/hooks";
import { ButtonIcon } from "@ab.uitools/ui-library/components/ButtonIcon";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Menu } from "@ab.uitools/ui-library/components/Menu";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import type { TClickEventType } from "@ab.uitools/ui-library/types/globalTypes";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props extends ITemplate {
  index: number;
  refetch: () => void;
}

export const TemplateItem = (
  {
    id,
    name,
    index,
    refetch,
    transactionType,
  }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showErrorSnackbar, showSuccessSnackbar } = useNotify();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState<boolean>(false);
  const [rowId, setRowId] = useState<string | null>(null);
  const [actionMenuRef, setActionMenuRef] = useState<HTMLDivElement | null>(null);

  const [renameTemplate, { isLoading: isRenameLoading }] = useRenameTemplateMutation();

  const templateIcon = {
    AccountTransfer: CloudflareAsset.AccountTransfer,
    CardTransfer: CloudflareAsset.CardTransfer,
    OwnAccountTransfer: CloudflareAsset.OwnAccountTransfer,
    BudgetTransfer: CloudflareAsset.BudgetTransfer,
    CurrencyExchange: CloudflareAsset.CurrencyExchange,
    GroupTransfer: CloudflareAsset.GroupTransfer,
    LoanRepayment: CloudflareAsset.LoanRepayment,
  };

  const openActionMenu = (e: TClickEventType) => {
    e.stopPropagation();
    setRowId(id);
    setIsOpen(prev => !prev);
  };

  const onRename = (name: string) => {
    renameTemplate({ id, name })
      .unwrap()
      .then(() => {
        setIsRenameModalOpen(false);
        refetch();
        showSuccessSnackbar(t("alerts.templateHasBeenSuccessfullyRenamed"));
      })
      .catch((err) => {
        showErrorSnackbar(err);
        setIsDeleteModalOpen(false);
      });
  };

  const menuItems = [
    {
      label: t("general.rename"),
      value: "template_rename",
      handler: () => {
        setIsRenameModalOpen(true);
      },
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.Rename}/>
        ),
      }
    },
    {
      label: t("general.edit"),
      value: "template_edit",
      handler: () => {
        const path = OperationRoutes?.[`${transactionType}TemplateUpdate`];
        if (!path) return;
        const url = buildRoute(path, { operationId: id });
        navigate(`${url}?templateName=${name}`);
      },
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.Edit}/>
        ),
      }
    },
    {
      label: t("general.delete"),
      value: "template_delete",
      handler: () => {
        setRowId(id);
        setIsDeleteModalOpen(true);
      },
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.Trash}/>
        ),
      }
    },
  ];

  return (
    <div className="flexbox align-items--center pl-32 pr-32 widget-content-item">
      <div className="item-icon flexbox align-items--center justify-content--center">
        <div className="flexbox align-items--center justify-content--center">
          <CloudFlareImage path={templateIcon[transactionType as keyof typeof templateIcon]}/>
        </div>
      </div>
      <div className="flexbox flex-direction--column pl-16 template-item-content-wrapper">
        {!!index && (
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        )}
        <div className="flexbox full-width template-item-body-wrapper">
          <div className="flexbox flex-direction--column full-width">
            <div className="full-width flexbox">
              <Text className="template-name pr-16 truncate" size="medium" weight="semibold">
                {name}
              </Text>
            </div>
            <div className="flexbox align-items--center">
              <Text
                className="text-nowrap truncate mt-4 text-nowrap"
                type="tertiary"
              >
                {transactionType}
              </Text>
            </div>
          </div>
          <div ref={setActionMenuRef} className="flexbox align-items--center">
            <ButtonIcon
              onClick={openActionMenu}
              data-testid="open-template-item-action-menu"
              data-ga-id="open-template-item-action-menu"
              data-ga-action="Open template item action menu"
              size="large"
              className={classNames("item-action-button", {
                "active": isOpen,
              })}
              iconProps={{
                Component: MoreIcon,
                type: "secondary",
              }}
            />
            <Menu
              position={Positions.BOTTOM_RIGHT}
              onClose={() => setIsOpen(false)}
              isOpen={isOpen}
              parentRef={actionMenuRef}
              additionalRef={actionMenuRef}
              menuItems={menuItems}
            />
          </div>
        </div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteTemplateModal
          id={rowId}
          setId={setRowId}
          refetch={refetch}
          templateName={name}
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
        />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <TemplateNameModal
          isRename
          isOpen={isRenameModalOpen}
          isLoading={isRenameLoading}
          onClose={() => setIsRenameModalOpen(false)}
          templateName={name}
          onConfirm={onRename}
          buttonText="general.save"
        />
      </div>
    </div>
  );
};
